// ═══════════════════════════════════════════════════════════════════════════
// SEARCH-UI — Logique + rendu PARTAGÉS de la recherche d'action.
// Monté dans un conteneur arbitraire : l'overlay Spotlight desktop
// (component-spotlight, ouvert depuis la loupe du header / Ctrl-⌘-K) ET la page
// #search responsive (page-search). Moteur serveur GET /actions/search
// (full-text FR + fuzzy pg_trgm). Surlignage reconstruit côté client à partir
// des termes (aucun HTML serveur) — chaque segment passe par esc().
// ═══════════════════════════════════════════════════════════════════════════

import { highlightSegments } from '../domain/search-highlight.js';
import { chipsBarHtml, searchShellHtml } from './search-ui-html.js';

interface SearchItem {
  id: number;
  text: string | null;
  status_name: string | null;
  deadline: string | null;
  deadline_revised: string | null;
  owner_trigram: string | null;
  approver_trigram: string | null;
  committee_codes?: string[] | null;
  is_confidential?: boolean;
}

export interface SearchSuggestion {
  type: 'person' | 'committee';
  id: number;
  label: string;
}

interface SearchResponse {
  groups: Array<{ key: string; label: string; total: number; items: SearchItem[] }>;
  suggestions: SearchSuggestion[];
}

export type Chip =
  | { kind: 'person'; id: number; label: string }
  | { kind: 'committee'; id: number; label: string }
  | { kind: 'mine'; label: string }
  | { kind: 'late'; label: string };

const DEBOUNCE_MS = 160;

export interface MountSearchOpts {
  // Appelé après navigation vers une action (ex. fermer l'overlay Spotlight).
  onPick?: () => void;
  // Focus l'input au montage (défaut true).
  autofocus?: boolean;
}

// Construit le querystring de /actions/search depuis la saisie + les chips.
function buildQuery(term: string, chips: Chip[]): string {
  const p = new URLSearchParams();
  if (term.trim()) p.set('q', term.trim());
  for (const c of chips) {
    if (c.kind === 'person') p.set('person_id', String(c.id));
    else if (c.kind === 'committee') p.set('committee_id', String(c.id));
    else if (c.kind === 'mine') p.set('mine', 'true');
    else if (c.kind === 'late') p.set('late_only', 'true');
  }
  const qs = p.toString();
  return qs ? '?' + qs : '';
}

// Rendu d'une ligne de résultat : texte surligné + méta (id, statut, owner,
// échéance, comités). Cible tactile généreuse pour l'usage au pouce.
function renderRow(it: SearchItem, term: string): string {
  const body = highlightSegments(it.text || '', term)
    .map((s) => (s.hit ? `<mark class="search-hit">${esc(s.text)}</mark>` : esc(s.text)))
    .join('');
  const meta: string[] = [`#${it.id}`];
  if (it.status_name) meta.push(esc(it.status_name));
  if (it.owner_trigram) meta.push(esc(it.owner_trigram));
  else if (it.approver_trigram) meta.push('A ' + esc(it.approver_trigram));
  const due = it.deadline_revised || it.deadline;
  if (due) meta.push(esc(fmtDate(due)));
  if (it.committee_codes && it.committee_codes.length)
    meta.push(esc(it.committee_codes.join(', ')));
  const lock = it.is_confidential
    ? `<span title="${esc(t('pageSearch.confidential'))}" style="opacity:.6" aria-hidden="true">🔒 </span>`
    : '';
  return `
    <button type="button" class="search-row" data-id="${it.id}"
      style="display:block;width:100%;text-align:left;background:none;border:none;border-bottom:1px solid var(--border,#e2e6ec);padding:12px 6px;cursor:pointer;min-height:44px;">
      <span style="display:block;font-size:15px;color:var(--text,#1b2733);line-height:1.4;">${lock}${body || esc(t('pageSearch.untitled'))}</span>
      <span style="display:block;font-size:12px;color:var(--text3,#646e7e);margin-top:3px;">${esc(meta.join(' · '))}</span>
    </button>`;
}

// Peint l'état actif / inactif des deux quick-filters d'après les chips posés.
function paintQuickButtons(quickEl: HTMLElement, chips: readonly Chip[]): void {
  quickEl.querySelectorAll<HTMLButtonElement>('.search-quick').forEach((b) => {
    const kind = b.dataset.quick as 'mine' | 'late';
    const on = chips.some((c) => c.kind === kind);
    b.style.background = on ? 'var(--accent-bg,#e8f6f0)' : 'var(--bg2,#fff)';
    b.style.color = on ? 'var(--accent,#0f6e56)' : 'var(--text,#1b2733)';
    b.style.borderColor = on ? 'var(--accent,#1d9e75)' : 'var(--border,#d5dbe3)';
  });
}

// Monte l'interface de recherche complète dans `root` et câble tout le
// comportement (debounce, séquencement anti-réponse-périmée, chips, quick-filters,
// navigation). Sélecteurs scoping via classes (pas d'id) pour permettre plusieurs
// instances simultanées (page + overlay) sans collision.
export function mountSearchUI(root: HTMLElement, opts: MountSearchOpts = {}): void {
  root.innerHTML = searchShellHtml();

  const inputRaw = root.querySelector<HTMLInputElement>('.search-ui-input');
  const chipsRaw = root.querySelector<HTMLElement>('.search-ui-chips');
  const quickRaw = root.querySelector<HTMLElement>('.search-ui-quick');
  const resultsRaw = root.querySelector<HTMLElement>('.search-ui-results');
  if (!inputRaw || !chipsRaw || !quickRaw || !resultsRaw) return;
  // Consts post-garde : types non-null capturés pour les closures ci-dessous.
  const input = inputRaw;
  const chipsEl = chipsRaw;
  const quickEl = quickRaw;
  const resultsEl = resultsRaw;

  const chips: Chip[] = [];
  let suggestions: SearchSuggestion[] = [];
  let term = '';
  let seq = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  // ── Navigation clavier (↑/↓ sélection, ↵ ouvre) ──────────────────────────
  let selectedIndex = -1;
  const rowEls = (): HTMLElement[] =>
    Array.from(resultsEl.querySelectorAll<HTMLElement>('.search-row'));

  function paintSelection(): void {
    rowEls().forEach((r, i) => {
      r.style.background = i === selectedIndex ? 'var(--accent-bg,#e8f6f0)' : 'none';
    });
  }

  function setSelected(i: number, scroll = true): void {
    const rs = rowEls();
    if (rs.length === 0) {
      selectedIndex = -1;
      return;
    }
    selectedIndex = Math.max(0, Math.min(i, rs.length - 1));
    paintSelection();
    if (scroll) rs[selectedIndex]?.scrollIntoView({ block: 'nearest' });
  }

  function openRow(row: HTMLElement | null | undefined): void {
    const id = row?.dataset.id;
    if (!id) return;
    window.location.hash = '#action/' + id;
    opts.onPick?.();
  }

  function renderChips(): void {
    chipsEl.innerHTML = chipsBarHtml(chips, suggestions);
    paintQuickButtons(quickEl, chips);
  }

  function renderResults(resp: SearchResponse | null, loading: boolean): void {
    selectedIndex = -1;
    if (loading) {
      resultsEl.innerHTML = `<div class="loading" style="padding:16px 6px;color:var(--text3,#646e7e);">${esc(t('common.loading'))}</div>`;
      return;
    }
    if (!term.trim() && chips.length === 0) {
      resultsEl.innerHTML = `<div style="padding:24px 6px;color:var(--text3,#646e7e);font-size:14px;line-height:1.6;">${esc(t('pageSearch.hint'))}</div>`;
      return;
    }
    const group = resp?.groups?.[0];
    const items = group?.items || [];
    if (items.length === 0) {
      resultsEl.innerHTML = `<div style="padding:24px 6px;color:var(--text3,#646e7e);font-size:14px;">${esc(t('pageSearch.empty'))}</div>`;
      return;
    }
    const head = `<div style="font-size:12px;color:var(--text3,#646e7e);padding:8px 6px;">${esc(
      t('pageSearch.count', { n: group?.total ?? items.length }),
    )}</div>`;
    resultsEl.innerHTML = head + items.map((it) => renderRow(it, term)).join('');
    // Présélectionne le 1er résultat pour que ↵ ouvre sans toucher les flèches.
    setSelected(0, false);
  }

  async function runSearch(): Promise<void> {
    const mySeq = ++seq;
    if (!term.trim() && chips.length === 0) {
      suggestions = [];
      renderChips();
      renderResults(null, false);
      return;
    }
    renderResults(null, true);
    try {
      const resp = (await api.searchActions(buildQuery(term, chips))) as SearchResponse;
      // Réponse périmée : une frappe plus récente a pris la main -> on l'ignore.
      if (mySeq !== seq) return;
      suggestions = resp.suggestions || [];
      renderChips();
      renderResults(resp, false);
    } catch (e: unknown) {
      if (mySeq !== seq) return;
      resultsEl.innerHTML = `<div class="error" style="padding:16px 6px;">${esc(t('pageSearch.error'))}</div>`;
      safeWarn('searchUI runSearch', e);
    }
  }

  const schedule = (): void => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void runSearch(), DEBOUNCE_MS);
  };

  input.addEventListener('input', () => {
    term = input.value;
    schedule();
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected(selectedIndex + 1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected(selectedIndex - 1);
      return;
    }
    if (e.key === 'Enter') {
      const rs = rowEls();
      if (rs.length) {
        e.preventDefault();
        openRow(rs[selectedIndex >= 0 ? selectedIndex : 0]);
      }
      return;
    }
    // Backspace sur saisie vide retire le dernier chip (parité desktop/étroit).
    if (e.key === 'Backspace' && input.value === '' && chips.length > 0) {
      chips.pop();
      renderChips();
      void runSearch();
    }
  });

  chipsEl.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('button');
    if (!el) return;
    if (el.classList.contains('search-chip-active')) {
      const idx = Number(el.dataset.idx);
      if (!Number.isNaN(idx)) {
        chips.splice(idx, 1);
        renderChips();
        void runSearch();
      }
    } else if (el.classList.contains('search-suggest')) {
      const type = el.dataset.type as 'person' | 'committee';
      const id = Number(el.dataset.id);
      const label = el.dataset.label || '';
      const already = chips.some((c) => 'id' in c && c.id === id && c.kind === type);
      if (!Number.isNaN(id) && !already) {
        chips.push({ kind: type, id, label } as Chip);
        input.focus();
        renderChips();
        void runSearch();
      }
    }
  });

  quickEl.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLButtonElement>('.search-quick');
    if (!el) return;
    const kind = el.dataset.quick as 'mine' | 'late';
    const at = chips.findIndex((c) => c.kind === kind);
    if (at >= 0) chips.splice(at, 1);
    else
      chips.push({
        kind,
        label: kind === 'mine' ? t('pageSearch.quickMine') : t('pageSearch.quickLate'),
      } as Chip);
    renderChips();
    void runSearch();
  });

  resultsEl.addEventListener('click', (e) => {
    openRow((e.target as HTMLElement).closest<HTMLElement>('.search-row'));
  });

  // Survol souris = synchronise la sélection (clavier et souris ne se battent pas).
  resultsEl.addEventListener('mouseover', (e) => {
    const row = (e.target as HTMLElement).closest<HTMLElement>('.search-row');
    if (!row) return;
    const idx = rowEls().indexOf(row);
    if (idx >= 0 && idx !== selectedIndex) {
      selectedIndex = idx;
      paintSelection();
    }
  });

  renderChips();
  renderResults(null, false);
  if (opts.autofocus !== false) input.focus();
}
